import { useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Alert, { type AlertColor } from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button } from '@/shared/components/ui';
import { brand } from '@/shared/config/brand';
import type { GuideCardData } from '../types';
import { useInlineImage } from '../hooks/useInlineImage';
import { exportCardPdf, exportCardPng, printCard } from '../utils/cardExport';
import { cardFileName } from '../utils/guideCardData';
import { copyTextToClipboard, shareIdentity } from '../utils/identityUrl';
import { PrintableGuideCard } from './PrintableGuideCard';

type CardTask = 'pdf' | 'png' | 'print';

interface GuideCardActionsProps {
  card: GuideCardData;
  /** Optional secondary navigation, e.g. the full public profile. */
  profileLink?: { to: string; label: string } | null;
  /** 'stacked' keeps every action full-width for narrow side panels. */
  layout?: 'responsive' | 'stacked';
}

export function GuideCardActions({ card, profileLink, layout = 'responsive' }: GuideCardActionsProps) {
  const stacked = layout === 'stacked';
  const cardRef = useRef<HTMLDivElement>(null);
  const lockRef = useRef(false);
  const [busy, setBusy] = useState<CardTask | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; severity: AlertColor } | null>(null);

  const photo = useInlineImage(card.photo);
  const logo = useInlineImage(brand.logoUrl);
  const assetsReady = photo.settled && logo.settled;
  const exportDisabled = !assetsReady || busy !== null;

  const notify = (message: string, severity: AlertColor) => setFeedback({ message, severity });

  const run = async (
    task: CardTask,
    action: (node: HTMLElement) => Promise<void>,
    messages: { success?: string; failure: string }
  ) => {
    const node = cardRef.current;
    // The ref lock blocks double-clicks that land before `busy` re-renders.
    if (lockRef.current || !node || !assetsReady) return;
    lockRef.current = true;
    setBusy(task);
    try {
      await action(node);
      console.info('[GUIDE_IDENTITY_CARD_EXPORTED]', { code: card.code, format: task });
      if (messages.success) notify(messages.success, 'success');
    } catch (error) {
      console.error('[GUIDE_IDENTITY_CARD_EXPORT_FAILED]', { code: card.code, format: task, error });
      notify(messages.failure, 'error');
    } finally {
      lockRef.current = false;
      setBusy(null);
    }
  };

  const handlePdf = () =>
    run(
      'pdf',
      (node) => exportCardPdf(node, cardFileName(card.code, 'pdf'), `${card.name} — Trplin Guide Identity`),
      { success: 'ID card downloaded successfully.', failure: 'Unable to generate the PDF. Please try again.' }
    );

  const handlePng = () =>
    run('png', (node) => exportCardPng(node, cardFileName(card.code, 'png')), {
      success: 'ID card downloaded successfully.',
      failure: 'Unable to generate the image. Please try again.',
    });

  const handlePrint = () =>
    run('print', printCard, { failure: 'Unable to open the print dialog. Please try again.' });

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(card.identityUrl);
    notify(
      ok ? 'Profile link copied.' : 'Copying is not available in this browser. Please copy the link manually.',
      ok ? 'success' : 'error'
    );
  };

  const handleShare = async () => {
    const outcome = await shareIdentity({
      title: `${card.name} — Verified Trplin Guide`,
      text: `Verify ${card.name}'s Trplin Guide identity`,
      url: card.identityUrl,
    });
    if (outcome === 'copied') notify('Sharing is not available here — profile link copied instead.', 'success');
    if (outcome === 'failed') notify('Unable to share. Please copy the link instead.', 'error');
  };

  const taskIcon = (task: CardTask, icon: ReactNode) =>
    busy === task ? <CircularProgress size={18} color="inherit" aria-hidden /> : icon;

  return (
    <Box component="section" aria-label="Identity card actions">
      <PrintableGuideCard card={card} photoSrc={photo.src} logoSrc={logo.src} cardRef={cardRef} />

      <Box sx={{ display: 'grid', gridTemplateColumns: stacked ? '1fr' : { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
        <Button
          intent="primary-dark"
          fullWidth
          startIcon={taskIcon('pdf', <PictureAsPdfOutlinedIcon />)}
          onClick={handlePdf}
          disabled={exportDisabled}
          aria-busy={busy === 'pdf'}
        >
          {busy === 'pdf' ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
        <Button
          intent="secondary-outlined"
          fullWidth
          startIcon={taskIcon('png', <ImageOutlinedIcon />)}
          onClick={handlePng}
          disabled={exportDisabled}
          aria-busy={busy === 'png'}
        >
          {busy === 'png' ? 'Preparing PNG…' : 'Download PNG'}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 1,
          mt: 1.25,
        }}
      >
        <Button
          intent="ghost"
          fullWidth
          startIcon={taskIcon('print', <PrintOutlinedIcon />)}
          onClick={handlePrint}
          disabled={exportDisabled}
          aria-busy={busy === 'print'}
        >
          Print ID card
        </Button>
        <Button intent="ghost" fullWidth startIcon={<ShareOutlinedIcon />} onClick={handleShare}>
          Share guide card
        </Button>
        <Button intent="ghost" fullWidth startIcon={<ContentCopyIcon />} onClick={handleCopy}>
          Copy profile link
        </Button>
      </Box>

      {profileLink ? (
        <Button
          component={RouterLink}
          to={profileLink.to}
          intent="ghost"
          fullWidth
          startIcon={<OpenInNewIcon />}
          sx={{ mt: 1 }}
        >
          {profileLink.label}
        </Button>
      ) : null}

      {!assetsReady ? (
        <Typography variant="caption" color="text.secondary" role="status" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
          Preparing your card for download…
        </Typography>
      ) : null}

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={4000}
        onClose={(_, reason) => reason !== 'clickaway' && setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {feedback ? (
          <Alert
            severity={feedback.severity}
            variant="filled"
            role={feedback.severity === 'error' ? 'alert' : 'status'}
            onClose={() => setFeedback(null)}
            sx={{ width: '100%' }}
          >
            {feedback.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
